import { Peer } from 'peerjs';
import Host, { primeMelody, primeCamera, discardPrimedCamera } from './host.js';
import { unlockCryAudio } from './cry-analyser.js';
import Client from './client.js';
import { keepScreenAwake, showConnectError, getStored, setStored, removeStored, loadResumableHostId, withViewTransition, prefersReducedMotion } from './utils.js';
import { fetchIceServers, keepIceFresh } from './ice.js';
import { signalOptions } from './signal.js';
import { initInstallBanner } from './install.js';
import { initUpdateWatcher } from './update.js';
import { track, trackPeerError, EVENTS } from './analytics.js';
import { checkHostPresence } from './push.js';
import { initI18n, t } from './i18n.js';

// --- Role & flow ------------------------------------------------------------
// The app has three stages, reflected as a `stage-*` class on <body>:
//   role   — the Step 1 picker (baby monitor vs parent)
//   host   — the baby monitor (QR to connect, then the live monitor)
//   client — the parent (scan a QR, then watch)
//
// Role is no longer inferred purely from the URL. A bare URL shows the picker;
// an incoming `#<hostId>` is treated as a deep link and drops the visitor
// straight into the parent flow (scanning the QR with the phone's own camera
// still works, and so does a shared link).
const deepLinkHostId = window.location.hash ? window.location.hash.substring(1) : null;

// --- Persistent host identity ----------------------------------------------
// PeerJS assigns a fresh random ID on every load, which means each host reload
// mints a new ID and silently invalidates the QR code / link a viewer already
// scanned. Persisting the host's ID lets it reclaim the same address after a
// reload or crash, so an existing viewer's stored link keeps working and the
// client's auto-reconnect finds the host again without a re-scan.
const HOST_ID_KEY = 'cammirror:host-id';

const loadStoredHostId = () => getStored(HOST_ID_KEY) || null;
const saveHostId = (id) => setStored(HOST_ID_KEY, id);
const clearHostId = () => removeStored(HOST_ID_KEY);

function createPeer(id, iceServers) {
  // signalOptions() pins us to our own broker. Without it PeerJS defaults to its
  // free public cloud, which has no fallback path if it goes down (see signal.js).
  const options = { ...signalOptions(), config: { iceServers } };
  return id ? new Peer(id, options) : new Peer(options);
}

let peer = null;
let currentIceServers = null;
let role = null; // 'host' | 'client'
let appStarted = false;

// How many times a host will retry reclaiming its stored id before giving up
// and minting a fresh one. The signaling broker holds the previous session's
// registration for a few seconds after a reload, so retrying the same id keeps
// existing viewer links/QRs and push subscriptions valid across a host reload.
const MAX_RECLAIM = 3;
let hostIdReclaimAttempts = 0;

// Resolves once ICE credentials have been fetched. Role selection can happen
// before the fetch finishes (the picker is interactive immediately), so we must
// not create a peer with null iceServers — that would silently drop TURN and
// break connections behind symmetric NATs. startRole() awaits this first.
let markIceReady;
const iceReady = new Promise((resolve) => { markIceReady = resolve; });

// Only these PeerJS error types permanently destroy the peer and warrant a
// blocking, full-screen message. Everything else is recoverable and must NOT
// tear down the UI: a single dropped ICE candidate on iOS Safari surfaces as a
// non-fatal 'webrtc' error even though the connection still completes.
const FATAL_ERROR_TYPES = new Set([
  'browser-incompatible',
  'invalid-id',
  'invalid-key',
  'unavailable-id',
  'ssl-unavailable',
  'server-error',
  'socket-error',
  'socket-closed',
]);

// Register the service worker so the app is installable and its shell loads
// offline. Parcel compiles ./sw.js as a separate service-worker bundle.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(new URL('sw.js', import.meta.url))
      .then((reg) => initUpdateWatcher(reg))
      .catch((err) => console.warn('[CAMMIRROR] Service worker registration failed:', err));
  });
}

// App-wide globals other modules read off `this`/window (kept from the original
// design so host.js / client.js / utils.js can share the live stream + peer).
(function (globals) {
  'use strict';
  globals.profile = null; // set once a role is chosen
  globals.activeVideoStream = null;
  globals.constraints = null;
  globals.lastVolumeLevel = 50;
  globals.peer = null;
  globals.hash = window.location.hash;
  globals.$video = document.querySelector('video');
}((1, eval)('this')));

// Translate static markup as soon as this module evaluates. Module scripts are
// deferred, so the DOM is already parsed here — doing it now (rather than on
// window.onload, which waits for fonts/icons) avoids a flash of English text
// for non-English users before their language is applied.
const locale = initI18n();

// The landing copy in #seo is pre-rendered per locale, so /de/, /nl/ and friends
// already match. The root page is the English one, but its chrome still follows
// navigator.languages — which would leave e.g. Dutch buttons sitting above
// English prose, and <html lang="nl"> on English content. Swap the copy to match.
// Dynamically imported so the other five locales' text stays out of the main
// bundle, and only fetched when it's actually needed (below the fold anyway).
if (locale !== 'en' && !window.location.pathname.replace(/\/+$/, '')) {
  import('./seo-content.js')
    .then(({ seoInnerHtml, content }) => {
      const seo = document.getElementById('seo');
      if (seo) seo.innerHTML = seoInnerHtml(locale);
      const hint = document.querySelector('.seo-scroll-label');
      if (hint) hint.textContent = content[locale].scrollHint;
    })
    .catch(() => {
      /* landing copy stays English; the app itself is unaffected */
    });
}

// The role picker can carry an optional explainer above the buttons, but only
// when the screen is tall enough to hold it. Three lengths live in i18n
// (short, medium, long); we show the LONGEST that still clears the fixed logo,
// and hide the block entirely if even the short one won't fit. Measuring the
// live DOM (instead of hard-coding height breakpoints) keeps it correct across
// languages, fonts, dynamic browser chrome and safe-area insets.
//
// Why one geometric check is enough: only the two flexible spacers sit ABOVE the
// intro, so the buttons/safety block is always pinned to the bottom and can
// never be pushed off-screen by a longer intro. A too-long variant instead
// grows upward until its top collides with the logo. So "does it fit" reduces to
// "does its top clear the logo with a little breathing room".
const ROLE_INTRO_VARIANTS = ['long', 'medium', 'short'];
const ROLE_INTRO_MIN_GAP = 24; // px of breathing room to keep clear under the logo

function fitRoleIntro() {
  const intro = document.getElementById('role-intro');
  if (!intro) return;
  // Only relevant while the role picker is the visible screen.
  if (!document.body.classList.contains('stage-role')) { intro.hidden = true; return; }

  const logo = document.querySelector('.brand-logo');
  const logoBottom = logo ? logo.getBoundingClientRect().bottom : 0;
  intro.hidden = false;

  let chosen = null;
  for (const variant of ROLE_INTRO_VARIANTS) {
    intro.dataset.variant = variant;
    // Reading the rect forces the reflow, so this reflects the variant just set.
    const introTop = intro.getBoundingClientRect().top;
    if (introTop >= logoBottom + ROLE_INTRO_MIN_GAP) { chosen = variant; break; }
  }

  if (chosen) {
    intro.dataset.variant = chosen;
  } else {
    intro.hidden = true;
    delete intro.dataset.variant;
  }
}

let roleIntroRaf = 0;
function scheduleRoleIntroFit() {
  if (roleIntroRaf) cancelAnimationFrame(roleIntroRaf);
  roleIntroRaf = requestAnimationFrame(() => { roleIntroRaf = 0; fitRoleIntro(); });
}

function setStage(stage) {
  // Mirror the stage onto <html> as well as <body>: in an installed iOS PWA the
  // safe-area insets are painted from the root element's background, so the
  // role screen's warm bottom bar is driven by html.stage-role (see styles.css).
  [document.documentElement, document.body].forEach((el) => {
    el.classList.remove('stage-role', 'stage-host', 'stage-client');
    el.classList.add(`stage-${stage}`);
  });
}

// Reset / back: reload the app at the bare path (no hash) so every peer, camera
// and timer is torn down cleanly and we land back on the role picker. Simpler
// and far more robust than unwinding live media by hand.
function resetToRole() {
  // Release a camera primed on the tap but never adopted (the reload below would
  // free it too, but stop it eagerly so the indicator can't linger mid-reload).
  discardPrimedCamera();
  window.location.assign(window.location.pathname);
}

// Enter the parent flow already aimed at a specific monitor — a shared/scanned
// deep link, or the "Resume watching" shortcut — showing "Connecting…" straight
// away. A cold deep link sets the stage directly (there's no picker to morph
// from); a tap from the picker leaves the stage switch to startRole so it can
// morph. If the monitor is off, the client falls back to the scanner.
function connectToHost(hostId, { fromPicker = false, resumed = false } = {}) {
  if (!fromPicker) setStage('client');
  document.body.classList.add('connecting');
  startRole('client', { hostId, resumed });
}

function wirePeer(p, onOpen) {
  p.on('open', (id) => {
    if (role === 'host') saveHostId(id);
    hostIdReclaimAttempts = 0; // reclaimed (or claimed) successfully
    onOpen(id);
  });

  p.on('error', (err) => {
    if (role === 'host' && err.type === 'unavailable-id' && !appStarted) {
      const storedId = loadStoredHostId();
      // The broker still has our previous session's registration for a few
      // seconds after a reload. Retry the SAME id first so a host reload doesn't
      // silently orphan viewers (their stored link/QR + push subscription are
      // keyed to this id). Only mint a fresh id once reclaim clearly won't land.
      if (storedId && hostIdReclaimAttempts < MAX_RECLAIM) {
        hostIdReclaimAttempts += 1;
        const delay = 1500 * hostIdReclaimAttempts;
        console.warn(`[CAMMIRROR] Host ID busy; reclaim attempt ${hostIdReclaimAttempts}/${MAX_RECLAIM} in ${delay}ms`);
        try { peer.destroy(); } catch (e) { /* already gone */ }
        setTimeout(() => {
          if (appStarted) return; // a peer opened meanwhile — nothing to do
          peer = createPeer(storedId, currentIceServers);
          window.peer = peer;
          wirePeer(peer, onOpen);
          keepIceFresh(peer);
        }, delay);
        return;
      }
      console.warn('[CAMMIRROR] Stored host ID unavailable, requesting a fresh one.');
      clearHostId();
      try { peer.destroy(); } catch (e) { /* already gone */ }
      peer = createPeer(null, currentIceServers);
      window.peer = peer;
      wirePeer(peer, onOpen);
      keepIceFresh(peer);
      return;
    }
    const fatal = FATAL_ERROR_TYPES.has(err.type);
    trackPeerError(err.type, fatal);
    if (fatal) {
      // The peer died before Host could adopt the primed camera — free it so it
      // doesn't stay live behind the error screen. No-op if Host already took it.
      discardPrimedCamera();
      showConnectError(t('error.connection'), { backLabel: t('common.back') });
    } else {
      console.warn(`[CAMMIRROR] Recoverable peer error (${err.type}): ${err.message}`);
    }
  });

  p.on('disconnected', () => {
    console.warn('[CAMMIRROR] Peer disconnected, attempting to reconnect...');
    p.reconnect();
  });
}

// Bring up a peer for the chosen role and start Host/Client once it opens.
async function startRole(chosen, opts = {}) {
  if (role) return; // lock in the first choice synchronously (ignores double taps)
  role = chosen;
  profile = chosen;
  // Reveal the chosen screen. When the tap comes from the role picker, morph
  // into it — the logo and QR card carry a shared view-transition-name — for a
  // native push feel. A deep link opens on a fresh load with no prior screen to
  // morph from, so there we just apply the stage directly.
  const fromPicker = document.body.classList.contains('stage-role');
  // The role screen is the only scrollable stage (it carries the landing copy in
  // #seo); every other stage re-locks scrolling. Rewind before the transition
  // starts, not inside it — startViewTransition snapshots the old state up front,
  // so scrolling later would cross-fade between two different scroll offsets and
  // the screen would visibly lurch. A deep link always loads at the top already.
  if (fromPicker) {
    try { window.scrollTo(0, 0); } catch (e) { /* non-fatal */ }
  }
  const enterRole = () => {
    setStage(chosen);
    // Parent with no target yet: show the scanner in the same transition so the
    // logo morphs straight into place instead of flashing a blank frame while
    // the peer opens. Client re-adds this class idempotently once it's built.
    if (chosen === 'client' && !opts.hostId) {
      document.body.classList.add('need-scan');
      // "Open camera" can't work until the Client is constructed and wires it
      // (getUserMedia must run under the real tap, so we can't defer the tap).
      // Disable it up front so the early-revealed scanner never shows a dead
      // button; client.js re-enables it the moment the handler is attached.
      const startScan = document.getElementById('start-scan');
      if (startScan) startScan.disabled = true;
    }
  };
  if (fromPicker) withViewTransition(enterRole);
  else enterRole();
  track(EVENTS.APP_OPENED, { role: chosen });
  track(EVENTS.ROLE_SELECTED, { role: chosen, deepLink: !!opts.hostId, resumed: !!opts.resumed });

  if (chosen === 'host') initInstallBanner();

  // Never create the peer before TURN credentials are in hand.
  if (!currentIceServers) await iceReady;

  const peerId = chosen === 'host' ? loadStoredHostId() : null;
  peer = createPeer(peerId, currentIceServers);
  window.peer = peer;

  wirePeer(peer, () => {
    if (appStarted) return;
    appStarted = true;
    keepScreenAwake();
    if (chosen === 'host') {
      new Host(peer);
    } else {
      new Client(peer, opts.hostId || null);
    }
  });
  keepIceFresh(peer);
}

function wireRolePicker() {
  const chooseHost = document.getElementById('choose-host');
  const chooseClient = document.getElementById('choose-client');
  if (chooseHost) chooseHost.addEventListener('click', () => {
    // Advance to the monitor screen FIRST, so nothing about audio priming can
    // hold up the role transition (startRole's stage switch is synchronous).
    startRole('host');
    // Acquire the camera/mic NOW, inside this real tap, so iOS Safari shows the
    // permission prompt under user activation. Deferring it until Host is built
    // (after the async ICE fetch + peer 'open') loses the activation and iOS
    // then rejects getUserMedia with NotAllowedError. Best-effort: startCamera()
    // falls back to a fresh request if this didn't run.
    try { primeCamera(); } catch (e) { /* non-fatal */ }
    // Unlock the shared cry-detection AudioContext under the same activation.
    // Without this, iOS leaves it 'suspended' and the host silently never
    // analyses its mic — no cry alerts ever fire. Best-effort.
    try { unlockCryAudio(); } catch (e) { /* non-fatal */ }
    // Then, still inside this real tap, unlock the lullaby element so a parent
    // can start it remotely later — the monitor won't have a fresh gesture at
    // that point. Best-effort: never let it break role selection.
    try { primeMelody(); } catch (e) { /* non-fatal */ }
  });
  if (chooseClient) chooseClient.addEventListener('click', () => startRole('client'));

  // Returning parent shortcut: if this device recently watched a monitor, offer a
  // one-tap resume so a parent whose app was closed rejoins the still-running
  // monitor without going back to the room to re-scan. Same connecting flow as a
  // deep link; if the monitor is off, the client falls back to the scanner.
  const resumeWatch = document.getElementById('resume-watch');
  const resumeHostId = loadResumableHostId();
  if (resumeWatch && resumeHostId) {
    resumeWatch.addEventListener('click', () => connectToHost(resumeHostId, { fromPicker: true, resumed: true }));
    // Only surface the shortcut once we've confirmed the monitor is still
    // checking in with the backend. A monitor that's already off would otherwise
    // drop the parent into "Connecting…" that only fails over to the scanner
    // after several dead dials. Presence unknown (backend unreachable / not
    // configured, e.g. local dev) keeps the old optimistic behaviour so the
    // shortcut still appears. Left hidden until we know, so we never flash a
    // resume for a dead monitor.
    checkHostPresence(resumeHostId).then((live) => {
      if (live !== false) resumeWatch.hidden = false;
      // Confirmed live -> show the subtle pulsing dot. Unknown (null) still
      // reveals the shortcut optimistically, but without the dot, so we never
      // signal "live" when we couldn't actually verify it.
      if (live === true) resumeWatch.classList.add('is-live');
    });
  }

  // Scroll down to the landing copy. A button, not an <a href="#seo">, because
  // any location.hash is read as a host id (see deepLinkHostId), so a fragment
  // link would send the visitor into the parent "Connecting…" flow instead.
  const seoScroll = document.getElementById('seo-scroll');
  const seo = document.getElementById('seo');
  if (seoScroll && seo) {
    seoScroll.addEventListener('click', () => {
      // Browsers don't derive scroll behaviour from prefers-reduced-motion, so an
      // explicit 'smooth' would animate for people who asked us not to.
      seo.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  }

  // Safety explainer overlay.
  const safetyLink = document.getElementById('safety-link');
  const safetyOverlay = document.getElementById('safety-overlay');
  const openSafety = () => { if (safetyOverlay) safetyOverlay.hidden = false; };
  const closeSafety = () => { if (safetyOverlay) safetyOverlay.hidden = true; };
  if (safetyLink) safetyLink.addEventListener('click', openSafety);
  if (safetyOverlay) {
    safetyOverlay.addEventListener('click', (e) => {
      if (e.target === safetyOverlay || e.target.closest('.overlay-close')) closeSafety();
    });
  }

  // Reset / back buttons (host connect card + parent scan screen).
  document.querySelectorAll('.reset-btn').forEach((btn) =>
    btn.addEventListener('click', resetToRole)
  );
}

// Wire the picker and handle a deep link as soon as this module evaluates.
// Module scripts are deferred, so the DOM is already parsed here — doing this
// now instead of on window.onload (which waits for icons/fonts) means the role
// buttons are interactive immediately, even on a slow connection.
function boot() {
  wireRolePicker();
  // Deep link: skip the picker entirely and show "Connecting…" right away so a
  // scanned/shared link doesn't flash the role screen while ICE is fetched.
  if (deepLinkHostId) {
    connectToHost(deepLinkHostId);
    return;
  }
  // Otherwise we stay on the role picker (body defaults to `stage-role`). A
  // returning parent gets a one-tap "Resume watching your monitor" shortcut
  // there (see wireRolePicker) so they never have to walk back to the room to
  // re-scan a still-running monitor.
  //
  // Size the optional explainer to the available space now, and keep it in sync
  // as the viewport changes (rotation, browser chrome collapsing, keyboard).
  // Re-run once fonts load too, since text metrics shift the fit.
  fitRoleIntro();
  window.addEventListener('resize', scheduleRoleIntroFit);
  window.addEventListener('orientationchange', scheduleRoleIntroFit);
  if (window.visualViewport) window.visualViewport.addEventListener('resize', scheduleRoleIntroFit);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleRoleIntroFit).catch(() => {});
  // The overlap check depends on the logo's measured height, so re-fit once it
  // has actually loaded (its box is 0 before then).
  const brandLogo = document.querySelector('.brand-logo');
  if (brandLogo && !brandLogo.complete) brandLogo.addEventListener('load', scheduleRoleIntroFit, { once: true });
}
boot();

// Fetch TURN credentials in the background. startRole() awaits iceReady before
// creating any peer, so a slow/failed fetch (STUN-only fallback, see ice.js)
// never blocks the picker from being usable.
(async () => {
  currentIceServers = await fetchIceServers();
  markIceReady();
})();
